import { ChartPie } from 'lucide-react'
import { motion } from 'motion/react'
import SiteHealthActiveAudit from '@/components/site-health/SiteHealthActiveAudit'
import SiteHealthAuditForm from '@/components/site-health/SiteHealthAuditForm'
import SiteHealthAuditHistory from '@/components/site-health/SiteHealthAuditHistory'
import { staggerContainer, staggerItem } from '@/lib/motion/variants'
import { isActiveSiteHealthAuditStatus } from '@/lib/siteHealth'
import { useSiteHealthAudits } from '@/lib/useSiteHealth'

export default function SiteHealth() {
  const auditsQuery = useSiteHealthAudits()
  const activeAudits =
    auditsQuery.data?.audits.filter((audit) =>
      isActiveSiteHealthAuditStatus(audit.status)
    ) ?? []
  const terminalAudits =
    auditsQuery.data?.audits.filter(
      (audit) => !isActiveSiteHealthAuditStatus(audit.status)
    ) ?? []
  const siteQuota = auditsQuery.data?.quota.site
  const unlimitedAudits = auditsQuery.data?.quota.unlimited === true

  return (
    <motion.main
      variants={staggerContainer}
      initial="hidden"
      animate="shown"
      className="mx-auto w-full max-w-7xl px-6 pb-20 pt-10"
    >
      <motion.header
        variants={staggerItem}
        className="flex flex-col justify-between gap-6 sm:flex-row sm:items-start"
      >
        <div>
          <h1 className="text-4xl font-semibold tracking-tight">Site Health</h1>
          <p className="mt-2 max-w-2xl text-base leading-6 text-muted-foreground">
            Audit how AI systems discover, understand, and cite your site.
          </p>
        </div>

        {siteQuota && (
          <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-primary shadow-sm">
            <ChartPie className="size-5" aria-hidden="true" />
            <p className="text-sm font-medium tabular-nums">
              {unlimitedAudits
                ? 'Unlimited local audits'
                : `${siteQuota.used} of ${siteQuota.limit} site audits used`}
            </p>
          </div>
        )}
      </motion.header>

      <div className="mt-8 space-y-6">
        <motion.div variants={staggerItem}>
          <SiteHealthAuditForm
            disabled={!unlimitedAudits && activeAudits.length > 0}
          />
        </motion.div>

        {activeAudits.map((audit) => (
          <motion.div key={audit.auditId} variants={staggerItem}>
            <SiteHealthActiveAudit audit={audit} />
          </motion.div>
        ))}

        <motion.div variants={staggerItem}>
          <SiteHealthAuditHistory
            audits={terminalAudits}
            loading={auditsQuery.isPending}
            error={auditsQuery.isError}
          />
        </motion.div>
      </div>
    </motion.main>
  )
}
